import React, {useRef, useEffect, useState, useMemo} from 'react';
import {Body} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {SubscriptionPlan} from '@/types/models/pricing/subscriptionPlan';
import {getPriceCurrency} from '@/views/content-studio/pricing/utils';
import DropdownButton, {
  Option,
} from '@/components/dropdown-button/DropdownButton';
import styles from './PlanSelector.module.scss';

type PlanSelectorProps = {
  presentedPlanList: SubscriptionPlan[];
  selectedSubscriptionPlan: SubscriptionPlan;
  handlePlanChange: (option: Option) => void;
};

function PlanSelector({
  presentedPlanList,
  selectedSubscriptionPlan,
  handlePlanChange,
}: PlanSelectorProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number | undefined>(undefined);

  const options = useMemo(
    () =>
      presentedPlanList.map((plan, index) => ({
        id: plan.id,
        label:
          index === 0
            ? `${plan.videos} ${translate('videos')}`
            : `${plan.videos} (${getPriceCurrency(
                plan.currency,
                plan.price
              )}${translate(`/mo`)})`,
        disabled: plan.isDisabled,
        analyticsName:
          index === 0
            ? `${plan.videos} videos`
            : `${plan.videos} (${getPriceCurrency(
                plan.currency,
                plan.price
              )}/mo)`,
      })),
    [presentedPlanList]
  );

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  return (
    <div ref={buttonRef} style={{width: '100%'}}>
      <DropdownButton
        testID="plans-dropdown-button"
        buttonClassName={styles.button}
        label={
          <Body size="md">
            {`${selectedSubscriptionPlan.videos} ${translate('videos')}`}
          </Body>
        }
        options={options}
        onChange={handlePlanChange}
        selectedOptionIds={[selectedSubscriptionPlan.id]}
        menuWidth={buttonWidth}
        renderMenuItem={(option: Option) => <MenuItem option={option} />}
        menuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
          marginThreshold: -20,
          PaperProps: {
            className: styles.menu,
          },
        }}
      />
    </div>
  );
}

type MenuItemProps = {
  option: Option;
};

function MenuItem({option}: MenuItemProps) {
  return (
    <Body size="md" className={`${option.disabled ? styles.disabledPlan : ''}`}>
      {option.label}
    </Body>
  );
}

export default PlanSelector;
